import React from 'react';
import { useRouter } from 'next/router';
import Skeleton from 'react-loading-skeleton';
import { get_ } from 'sf-modules';
import { initializeApollo } from '@yema/commerce/lib/apollo';
import CMS_Page from 'components/cms/CMS_Page';
import Error500 from '@yema/commerce/components/checkout/Error500';
import { getBlogMostRecentPostsWithSlug, getBlogPost } from 'functions/fnRequestAPIs/fnCMSPagesAPI';
import { getMenus } from 'functions/fnRequestAPIs/fnMenus';
import BlogLayout from 'layouts/Blog.layout';
import Banner from '@/yema-designkit/webkit/molecules/Banner';
import NotFound from '@/yema-designkit/webkit/molecules/NotFound';
import Section from '@/yema-designkit/webkit/layouts/Section';

const PostSkeleton = () => {
    const duration = 1.2;
    return (
        <Section className='pb-0'>
            <div className='container'>
                <h2>
                    <Skeleton duration={duration} width='75%' height={42} />
                </h2>
            </div>
            <div className='container mb-12'>
                <Skeleton duration={duration} />
                <div className='mb-12' />
                <Skeleton duration={duration} count={15} />
            </div>
        </Section>
    );
};

const PostContent = ({ post }) => {
    const { title, publishedDate, seoImage } = post;

    return (
        <>
            {/* HEADER BLOG */}
            <Banner bannerInfo={post} isBlog={true} />
            {/* BODY POST */}
            <CMS_Page content={post} />

            <span itemProp='mainEntityOfPage'>
                <meta itemProp='headline' content={title} />
            </span>
            <meta itemProp='datePublished' content={publishedDate} />
            <meta itemProp='dateModified' content={publishedDate} />
            <meta itemProp='author' content='YEMA' />
            <meta itemProp='image' content={get_(seoImage, ['file'], '')} />
            <span itemProp='publisher' itemScope itemType='http://schema.org/Organization'>
                <meta itemProp='name' content='YEMA' />
                <span itemProp='logo' itemScope itemType='https://schema.org/ImageObject'>
                    <meta
                        itemProp='url'
                        content='https://storage.googleapis.com/yema-assets/logo/YEMA_Logotipo_Cuadrado-01.jpg'
                    />
                </span>
            </span>
        </>
    );
};

const Post = ({ pageContent, staticContent }) => {
    const router = useRouter();

    let metaTags = staticContent?.metaTags;
    if (
        !router.isFallback &&
        ((pageContent && pageContent.error) || !pageContent || !pageContent.data)
    )
        metaTags = {
            title: 'Página no encontrada',
            shouldIndex: false,
            dismissSeoTags: true,
        };

    return (
        <BlogLayout
            className='blog'
            staticContent={{
                ...staticContent,
                metaTags: metaTags,
            }}
            itemScope
            itemType='http://schema.org/Article'
        >
            {router.isFallback ? (
                <PostSkeleton />
            ) : pageContent && pageContent.error ? (
                <Error500
                    title='Error'
                    extrasToTrack={{
                        errorType: 'Unexpected error',
                        code: pageContent.error?.statusCode,
                        fromServer: pageContent.error?.statusCode ? true : false,
                        errorDescription: pageContent.error?.message,
                    }}
                    buttonOnClick={() => router.push(router.pathname)}
                />
            ) : !pageContent || !pageContent.data ? (
                <NotFound />
            ) : (
                <PostContent post={get_(pageContent, ['data'])} />
            )}
        </BlogLayout>
    );
};

export async function getStaticProps({ params, preview = false }) {
    try {
        if (!params.name) return { notFound: true };

        const apolloClient = initializeApollo();
        const menus = await getMenus(null, apolloClient);
        const page = await getBlogPost(apolloClient, params.name, preview);

        if (!page || !page.data) return { notFound: true };

        const metaTags = {
            title: get_(page, ['data', 'seoTitle']),
            description: get_(page, ['data', 'searchDescription']),
            image: get_(page, ['data', 'seoImage', 'file']),
            shouldIndex: get_(page, ['data', 'seoShouldIndex']),
            canonicalUrl: get_(page, ['data', 'seoCanonicalUrl']),
        };

        return {
            props: {
                revalidatedOn: Date.now(),
                pageContent: { ...page },
                staticContent: {
                    ...menus,
                    metaTags,
                    preview: !!preview,
                },
            },

            // Re-generate the page:
            // - when a request comes in
            // - at most once every X seconds
            revalidate: 300,
        };
    } catch (error) {
        // The API most likely died
        console.error(error);
        return {
            props: {},
        };
    }
}

export async function getStaticPaths() {
    const apolloClient = initializeApollo();
    const allPosts = await getBlogMostRecentPostsWithSlug(apolloClient, 5);
    return {
        paths: allPosts?.map((slug) => `/blog/${slug}`) || [],
        // fallback: true,
        fallback: 'blocking',
    };
}

export default Post;
